import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { useMemo, useState } from "react";
import type { DocDetail, DocFaqItem } from "./types.js";

import { Markdown } from "./markdown.js";

/**
 * Readable FAQ viewer — **one question + answer per item**, never the raw
 * canonical `## Q / A` markdown dumped as text.
 *
 * Mirrors the customer portal's `FaqArticle` (search filter, expand/collapse
 * all, one expandable row per Q&A) but built on MUI `Accordion` so it rides the
 * dashboard theme. The server parses the body into `faq_items` via
 * `Documents::FaqParser`; this only presents them.
 *
 * Pinned by `FaqReader.test.tsx` and the FAQ case in `documentation.cy.ts`.
 */
export function FaqReader({ detail }: { detail: DocDetail }): JSX.Element {
  const items = useMemo<DocFaqItem[]>(() => detail.faq_items ?? [], [detail.faq_items]);
  const [query, setQuery] = useState("");
  const [openSet, setOpenSet] = useState<Set<string>>(() => new Set());

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.question.toLowerCase().includes(q) ||
        it.answer_markdown.toLowerCase().includes(q),
    );
  }, [items, query]);

  const expandAll = (): void => setOpenSet(new Set(items.map((i) => i.anchor)));
  const collapseAll = (): void => setOpenSet(new Set());

  const toggle = (anchor: string, next: boolean): void => {
    setOpenSet((prev) => {
      const out = new Set(prev);
      if (next) out.add(anchor);
      else out.delete(anchor);
      return out;
    });
  };

  return (
    <Box data-testid="faq-reader" sx={{ display: "grid", gap: 1.5 }}>
      {/* Toolbar — search + expand/collapse all + live count */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${items.length} question${items.length === 1 ? "" : "s"}…`}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          inputProps={{ "aria-label": "Filter questions" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="outlined" color="inherit" size="small" onClick={expandAll} sx={{ bgcolor: "action.hover" }}>
          Expand all
        </Button>
        <Button variant="outlined" color="inherit" size="small" onClick={collapseAll} sx={{ bgcolor: "action.hover" }}>
          Collapse all
        </Button>
        <Typography sx={{ ml: 0.5, fontSize: 12, color: "text.disabled" }} aria-live="polite">
          {query.trim() ? `${filtered.length} of ${items.length}` : `${items.length} questions`}
        </Typography>
      </Box>

      {filtered.length === 0 ? (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            No questions match “{query.trim()}”
          </Typography>
          <Button variant="text" size="small" onClick={() => setQuery("")} sx={{ mt: 0.5 }}>
            Clear the search
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1 }}>
          {filtered.map((item) => {
            const open = openSet.has(item.anchor);
            return (
              <Accordion
                key={item.anchor}
                id={`q-${item.anchor}`}
                expanded={open}
                onChange={(_, next) => toggle(item.anchor, next)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`a-${item.anchor}`}
                  sx={{ "& .MuiAccordionSummary-content": { my: 1.25 } }}
                >
                  <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  id={`a-${item.anchor}`}
                  sx={{ borderTop: 1, borderColor: "divider", pt: 1.5, fontSize: 14 }}
                >
                  <Markdown source={item.answer_markdown} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
